import { BASE_PATH } from './constans';
import type { ReportType } from '../components/common/reports/utils/downloadReport';

const ussdEndpoint = 'services/ussd';
const ussdReports = `${ussdEndpoint}/reports`;
const ussdApp = 'ussd/app';

const ussd = {
  ussdApp,
  ussdApps: 'ussd/apps',
  editUssdAppSettings: (ussdAppId: string) => `${ussdApp}/${ussdAppId}/settings`,
  ussdBilling: `${ussdEndpoint}/billing`,
  ussdReports,
  ussdReportsRegenerate: (reportId: string) => `${ussdReports}/${reportId}/regenerate`,
  ussdReportsRetry: (reportId: string) => `${ussdReports}/${reportId}/retry`,
  ussdOperations: (operationId: string) => `${ussdEndpoint}/operations/${operationId}`
};

const wapEndpoint = 'services/wap';
const wapReports = `${wapEndpoint}/reports`;
const wapApp = 'wap/app';

const wap = {
  wapApp,
  wapApps: 'wap/apps',
  editWapAppSettings: (wapAppId: string) => `${wapApp}/${wapAppId}/settings`,
  wapBilling: `${wapEndpoint}/billing`,
  wapReports,
  wapReportsRegenerate: (reportId: string) => `${wapReports}/${reportId}/regenerate`,
  wapReportsRetry: (reportId: string) => `${wapReports}/${reportId}/retry`,
  wapOperations: (operationId: string) => `${wapEndpoint}/operations/${operationId}`
};

const voiceEndpoint = 'services/voice';
const voiceReports = `${voiceEndpoint}/reports`;
const voiceApp = 'voice/app';

const voice = {
  voiceApp,
  voiceApps: 'voice/apps',
  editVoiceAppSettings: (voiceAppId: string) => `${voiceApp}/${voiceAppId}/settings`,
  voiceBilling: `${voiceEndpoint}/billing`,
  voiceReports,
  voiceReportsRegenerate: (reportId: string) => `${voiceReports}/${reportId}/regenerate`,
  voiceReportsRetry: (reportId: string) => `${voiceReports}/${reportId}/retry`,
  voiceOperations: (operationId: string) => `${voiceEndpoint}/operations/${operationId}`
};

const clientCreate = 'client/create';
const clientUpdate = 'client/update';
const clientSecretsRegenerate = 'client/secrets/regenerate';
const client = {
  clientInfo: 'client/info',
  clientStatus: 'client/status',
  clientBalance: 'client/balance',
  clientCreateOrder: `${clientCreate}/order`,
  clientCreateConfirm: `${clientCreate}/confirm`,
  clientCreateOtpResend: `${clientCreate}/otp/resend`,
  clientInvitationConfirm: `${clientCreate}/invitation/confirm`,
  clientSettingsUpdateOrder: `${clientUpdate}/order`,
  clientSettingsUpdateConfirm: `${clientUpdate}/confirm`,
  clientRegenerateSecretsOrder: `${clientSecretsRegenerate}/order`,
  clientRegenerateSecretsConfirm: `${clientSecretsRegenerate}/confirm`,
  clientLowBalance: `client/settings/lowBalance`
};

const walletCredits = 'wallet/credits';
const walletCreditsExternalTransfer = `${walletCredits}/externalTransfer`;
const wallet = {
  walletCreditExternalTransferOrder: `${walletCreditsExternalTransfer}/order`,
  walletCreditInfo: (creditOrderId: string) => `${walletCredits}/${creditOrderId}/info`,
  walletCreditExternalTransferReject: (creditOrderId: string) =>
    `${walletCreditsExternalTransfer}/${creditOrderId}/reject`
};

const usersEndpoint = 'users';
const accountsManagement = {
  accountsManagementActive: `${usersEndpoint}`,
  accountsManagementInactive: `${usersEndpoint}/invitations`,
  userInvitations: (invitationId: string) => `${usersEndpoint}/invitations/${invitationId}/resend`
};

const airtimeAndDataEndpoint = 'services/airtimeAndData';
const airtimeAndData = {
  airtimeAndDataReports: `${airtimeAndDataEndpoint}/reports`,
  airtimeAndDataBilling: `${airtimeAndDataEndpoint}/billing`,
  airtimeAndDataReportsRegenerate: (reportId: string) => `${airtimeAndDataEndpoint}/reports/${reportId}/regenerate`,
  airtimeAndDataReportsRetry: (reportId: string) => `${airtimeAndDataEndpoint}/reports/${reportId}/retry`,
  airtimeAndDataOperations: (operationId: string) => `${airtimeAndDataEndpoint}/operations/${operationId}`
};

const accountTransfersEndpoint = 'services/transfer';
const accountTransfers = {
  accountTransfersReports: `${accountTransfersEndpoint}/reports`,
  accountTransfersReportsRegenerate: (reportId: string) => `${accountTransfersEndpoint}/reports/${reportId}/regenerate`,
  accountTransfersReportsRetry: (reportId: string) => `${accountTransfersEndpoint}/reports/${reportId}/retry`,
  accountTransfersBilling: `${accountTransfersEndpoint}/billing`,
  accountTransfersOperations: (operationId: string) => `${accountTransfersEndpoint}/operations/${operationId}`
};

const smsEndpoint = 'services/sms';
const sms = {
  smsReports: `${smsEndpoint}/reports`,
  smsReportsRegenerate: (reportId: string) => `${smsEndpoint}/reports/${reportId}/regenerate`,
  smsReportsRetry: (reportId: string) => `${smsEndpoint}/reports/${reportId}/retry`,
  smsBilling: `${smsEndpoint}/billing`,
  smsOperations: (operationId: string) => `${smsEndpoint}/operations/${operationId}`
};

const operatorLookupEndpoint = 'services/operatorLookup';
const operatorLookup = {
  operatorLookupReports: `${operatorLookupEndpoint}/reports`,
  operatorLookupReportsRegenerate: (reportId: string) => `${operatorLookupEndpoint}/reports/${reportId}/regenerate`,
  operatorLookupReportsRetry: (reportId: string) => `${operatorLookupEndpoint}/reports/${reportId}/retry`,
  operatorLookupBilling: `${operatorLookupEndpoint}/billing`,
  operatorLookupOperations: (operationId: string) => `${operatorLookupEndpoint}/operations/${operationId}`
};

const onDemandEndpoint = 'services/onDemandPaymentAndAcquisition';
const onDemand = {
  onDemandBilling: `${onDemandEndpoint}/billing`,
  onDemandOperations: (operationId: string) => `${onDemandEndpoint}/operations/${operationId}`
};

const payoutEndpoint = 'services/payout';
const payout = {
  payoutBilling: `${payoutEndpoint}/billing`,
  payoutOperations: (operationId: string) => `${payoutEndpoint}/operations/${operationId}`,
  payoutReports: `${payoutEndpoint}/reports`,
  payoutReportsRegenerate: (reportId: string) => `${payoutEndpoint}/reports/${reportId}/regenerate`,
  payoutReportsRetry: (reportId: string) => `${payoutEndpoint}/reports/${reportId}/retry`
};

const merchantPaymentEndpoint = 'services/merchantPayment';
const merchantPayment = {
  merchantPaymentBilling: `${merchantPaymentEndpoint}/billing`,
  merchantPaymentReports: `${merchantPaymentEndpoint}/reports`,
  merchantPaymentReportsRegenerate: (reportId: string) => `${merchantPaymentEndpoint}/reports/${reportId}/regenerate`,
  merchantPaymentReportsRetry: (reportId: string) => `${merchantPaymentEndpoint}/reports/${reportId}/retry`,
  merchantPaymentOperations: (operationId: string) => `${merchantPaymentEndpoint}/operations/${operationId}`
};

const customerValidationEndpoint = 'services/customerValidation';
const customerValidation = {
  customerValidationBilling: `${customerValidationEndpoint}/billing`,
  customerValidationReports: `${customerValidationEndpoint}/reports`,
  customerValidationOperations: (operationId: string) => `${customerValidationEndpoint}/operations/${operationId}`,
  customerValidationReportsRegenerate: (reportId: string) =>
    `${customerValidationEndpoint}/reports/${reportId}/regenerate`,
  customerValidationReportsRetry: (reportId: string) => `${customerValidationEndpoint}/reports/${reportId}/retry`
};

const customerAuthenticationEndpoint = 'services/customerAuthentication';
const customerAuthentication = {
  customerAuthenticationReports: `${customerAuthenticationEndpoint}/reports`,
  customerAuthenticationReportsRegenerate: (reportId: string) =>
    `${customerAuthenticationEndpoint}/reports/${reportId}/regenerate`,
  customerAuthenticationReportsRetry: (reportId: string) =>
    `${customerAuthenticationEndpoint}/reports/${reportId}/retry`,
  customerAuthenticationBilling: `${customerAuthenticationEndpoint}/billing`,
  customerAuthenticationOperations: (operationId: string) =>
    `${customerAuthenticationEndpoint}/operations/${operationId}`
};
const customerEnrollmentEndpoint = 'services/customerEnrollment';
const customerEnrollment = {
  customerEnrollmentReports: `${customerEnrollmentEndpoint}/reports`,
  customerEnrollmentReportsRegenerate: (reportId: string) =>
    `${customerEnrollmentEndpoint}/reports/${reportId}/regenerate`,
  customerEnrollmentReportsRetry: (reportId: string) => `${customerEnrollmentEndpoint}/reports/${reportId}/retry`,
  customerEnrollmentBilling: `${customerEnrollmentEndpoint}/billing`,
  customerEnrollmentOperations: (operationId: string) => `${customerEnrollmentEndpoint}/operations/${operationId}`
};

export default {
  ...voice,
  ...wap,
  ...ussd,
  ...client,
  ...wallet,
  ...accountsManagement,
  ...airtimeAndData,
  ...sms,
  ...operatorLookup,
  ...accountTransfers,
  ...onDemand,
  ...payout,
  ...merchantPayment,
  ...customerValidation,
  ...customerAuthentication,
  ...customerEnrollment,
  walletCredits: 'wallet/credits',
  servicesCost: 'services/cost',
  operationHistoryLatest: 'operation/history/latest',
  reportDownload: (reportId: string, reportType: ReportType) =>
    `${window.location.origin}${BASE_PATH}/report/${reportType}/download/${reportId}`
};
